import {
  Component,
  Input,
  TemplateRef,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
  NgZone,
  HostBinding,
} from '@angular/core';

@Component({
  selector: 'app-hover-tooltip',
  templateUrl: './hover-tooltip.component.html',
  styleUrls: ['./hover-tooltip.component.scss'],
})
export class HoverTooltipComponent implements OnDestroy {
  @Input() contentTemplate: TemplateRef<unknown> | null = null;
  @Input() context: unknown = null;
  /** Delay before hiding after pointer leaves trigger/panel. Use 0 for instant hide (e.g. calendar layouts). */
  @Input() hideDelayMs = 200;

  @ViewChild('trigger') triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('panel') panelRef!: ElementRef<HTMLElement>;

  visible = false;
  panelLeft = 0;
  panelTop = 0;
  showAbove = false;

  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly gap = 6;
  private scrollUnsub: (() => void) | null = null;

  /** Lift this instance above sibling calendar cards while the panel is open. */
  @HostBinding('style.position')
  get hostPosition(): string | null {
    return this.visible ? 'relative' : null;
  }

  @HostBinding('style.z-index')
  get hostZIndex(): number | null {
    return this.visible ? 20000 : null;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.clearHideTimer();
    this.removeScrollListener();
  }

  onTriggerEnter(): void {
    this.clearHideTimer();
    if (!this.contentTemplate) return;
    this.visible = true;
    this.addScrollListener();
    this.cdr.detectChanges();
    this.zone.runOutsideAngular(() => {
      // Double rAF: first paints the panel, second measures real size for alignment.
      requestAnimationFrame(() => {
        this.updatePosition();
        this.cdr.detectChanges();
        requestAnimationFrame(() => {
          this.updatePosition();
          this.cdr.detectChanges();
        });
      });
    });
  }

  onTriggerLeave(event?: MouseEvent): void {
    // Allow moving pointer onto the panel without closing first (needed when hideDelayMs is 0).
    const rel = event?.relatedTarget;
    const panel = this.panelRef?.nativeElement;
    if (rel && panel instanceof Node && panel.contains(rel as Node)) {
      return;
    }
    this.scheduleHide();
  }

  onPanelEnter(): void {
    this.clearHideTimer();
  }

  onPanelLeave(): void {
    this.scheduleHide();
  }

  hide(): void {
    this.clearHideTimer();
    this.removeScrollListener();
    this.visible = false;
  }

  private updatePosition(): void {
    const trigger = this.triggerRef?.nativeElement;
    if (!trigger) return;
    const triggerRect = trigger.getBoundingClientRect();

    const panel = this.panelRef?.nativeElement;
    const panelWidth = panel?.offsetWidth || 220;
    const panelHeight = panel?.offsetHeight || 120;

    const viewW = window.innerWidth;
    const viewH = window.innerHeight;

    const spaceBelow = viewH - triggerRect.bottom - this.gap;
    const spaceAbove = triggerRect.top - this.gap;

    if (spaceBelow >= panelHeight || spaceBelow >= spaceAbove) {
      this.panelTop = triggerRect.bottom + this.gap;
      this.showAbove = false;
    } else {
      this.panelTop = Math.max(this.gap, triggerRect.top - panelHeight - this.gap);
      this.showAbove = true;
    }

    // Align to the trigger's left edge so the panel tracks the card, not mid-viewport.
    let left = triggerRect.left;
    const margin = 8;
    if (left + panelWidth > viewW - margin) {
      left = viewW - panelWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }
    this.panelLeft = left;
  }

  private addScrollListener(): void {
    if (this.scrollUnsub) return;
    const handler = () => {
      if (this.visible) {
        this.updatePosition();
        this.cdr.detectChanges();
      }
    };
    document.addEventListener('scroll', handler, true);
    this.scrollUnsub = () => document.removeEventListener('scroll', handler, true);
  }

  private removeScrollListener(): void {
    if (this.scrollUnsub) {
      this.scrollUnsub();
      this.scrollUnsub = null;
    }
  }

  private scheduleHide(): void {
    this.clearHideTimer();
    if (this.hideDelayMs <= 0) {
      this.hide();
      this.cdr.detectChanges();
      return;
    }
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.hide();
      this.cdr.detectChanges();
    }, this.hideDelayMs);
  }

  private clearHideTimer(): void {
    if (this.hideTimer != null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  get templateContext(): Record<string, unknown> {
    return { $implicit: this.context, hide: () => this.hide() };
  }
}
